import React, { useEffect, useState, useContext } from 'react'
import styled from '@emotion/styled/macro'
import PropTypes from 'prop-types'
import { useLazyQuery, useQuery } from '@apollo/client'
import { Redirect } from 'react-router-dom'
import throttle from 'lodash/throttle'
import { MessageFormat } from '@paypalcorp/worldready'
import { Context as WorldReadyContext } from '@paypalcorp/worldready-react'
import lodash from 'lodash'

import getAllProducts from '../../graphql/getAllProducts'
import getStoresByLocation from '../../graphql/getStoresByLocation'
import { handleSessionError } from '../../utils/error-tracking'
import VoucherSection from './VoucherSection'
import LocationBasedProductSection from './LocationBasedProductSection'

const BusinessProductsSection = styled.div`
  max-width: 852px;
  margin: auto;
`

const BusinessesSearch = ({ location, history }) => {
  const [filteredProductList, setFilteredProductList] = useState([])
  const { loading, data, error } = useQuery(getAllProducts)

  const [
    getStores,
    { loading: storesLoading, data: storesData, error: storeError },
  ] = useLazyQuery(getStoresByLocation)

  const queryParams = new URLSearchParams(location.search)
  const urlQuery = decodeURIComponent(queryParams.get('q') || '')
  const [searchLocationName, setSearchLocationName] = useState('')
  const [query, setQuery] = useState(urlQuery)
  const [showSpinner, setShowSpinner] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const worldready = useContext(WorldReadyContext)
  const MAX_PAGE_SIZE = 39
  const NO_LOCATION_DEFAULT_LOCATION_TEXT = 'default'

  const maxNumberOfPages = () =>
    Math.ceil(filteredProductList.length / MAX_PAGE_SIZE)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    try {
      const locationToSearch = JSON.parse(query)

      getStores({
        variables: {
          locationToSearch,
        },
      })
    } catch (err) {
      setFilteredProductList([])
      setSearchLocationName(NO_LOCATION_DEFAULT_LOCATION_TEXT)
    }
  }, [query])

  useEffect(() => {
    if (!data || !data.getAllProducts) return

    const setProductsAndSearchFilters = () => {
      const allGiftCards = lodash.get(data, 'getAllProducts.all_a_z', [])
      const stores = lodash.get(storesData, 'getStoresByLocation.stores', [])
      const locationAddress = lodash.get(
        storesData,
        'getStoresByLocation.locationAddress',
        null,
      )
      const parsedQuery = JSON.parse(query)

      const storeMerchantIDMap = stores.reduce(
        (map, store) => ({
          ...map,
          [store.merchantId]: store,
        }),
        {},
      )

      const allSMBGiftCards = allGiftCards.filter((gc) =>
        gc.categories.includes('Small Business'),
      )

      const filteredGiftCardsWithLocation = allSMBGiftCards.reduce(
        (giftCards, giftCard) => {
          const matchingStore = storeMerchantIDMap[giftCard.merchant_id]
          const giftCardWithAddress = {
            ...giftCard,
            address: lodash.get(matchingStore, 'address'),
          }

          if (matchingStore) return [...giftCards, giftCardWithAddress]
          return giftCards
        },
        [],
      )

      setFilteredProductList(filteredGiftCardsWithLocation)

      // if zip code search returned valid zip address
      if (locationAddress) {
        return setSearchLocationName(locationAddress)
      }

      const giftCardWithCityState = allSMBGiftCards.find(
        (gc) => gc.address && gc.address.city && gc.address.state,
      )

      // if found giftcard with city and state address
      if (giftCardWithCityState) {
        return setSearchLocationName(
          `${giftCardWithCityState.address.city}, ${giftCardWithCityState.address.state}`,
        )
      }

      // if query has zipcode
      if (parsedQuery.zipCode) {
        return setSearchLocationName(parsedQuery.zipCode)
      }

      // if query has lat and long
      if (parsedQuery.latitude && parsedQuery.longitude) {
        return setSearchLocationName(
          new MessageFormat(worldready, {
            id: 'pages/businesses.businesses.local.currentLocation',
          }).format(),
        )
      }

      return setSearchLocationName(NO_LOCATION_DEFAULT_LOCATION_TEXT)
    }

    setProductsAndSearchFilters()
  }, [data, storesData])

  const handleScroll = () => {
    const { body, documentElement } = document

    const scrollTop =
      window.pageYOffset !== undefined
        ? window.pageYOffset
        : (documentElement || body.parentNode || body).scrollTop
    const documentHeight = Math.max(
      body.scrollHeight,
      body.offsetHeight,
      documentElement.clientHeight,
      documentElement.scrollHeight,
      documentElement.offsetHeight,
    )
    if (scrollTop < documentHeight - window.innerHeight) return

    if (currentPage < maxNumberOfPages()) {
      setShowSpinner(true)
      setTimeout(() => {
        setCurrentPage(currentPage + 1)
        setShowSpinner(false)
      }, 500)
    }
  }

  useEffect(() => {
    const throttleScroll = throttle(handleScroll, 300)
    window.addEventListener('scroll', throttleScroll)
    return () => window.removeEventListener('scroll', throttleScroll)
  })

  if (loading || storesLoading || !searchLocationName) {
    return (
      <div className="vx_has-spinner-large" style={{ paddingTop: '400px' }} />
    )
  }

  if (error) {
    handleSessionError(error)
    return <Redirect to="/error" />
  }

  if (storeError) {
    handleSessionError(storeError)
    return <Redirect to="/error" />
  }

  const onSaveEditLocation = (value) => {
    const stringifiedUpdatedQuery = JSON.stringify(
      typeof value === 'string' ? { zipCode: value } : value,
    )

    if (value && stringifiedUpdatedQuery !== query) {
      setSearchLocationName('')
      setQuery(stringifiedUpdatedQuery)
      history.replace(
        `/business/search/?q=${encodeURIComponent(stringifiedUpdatedQuery)}`,
      )
    }
  }

  const getProductsInPage = () => {
    const maxAllowedInCurrentPage = currentPage * MAX_PAGE_SIZE
    const endIndex =
      filteredProductList.length > maxAllowedInCurrentPage
        ? maxAllowedInCurrentPage
        : filteredProductList.length
    return filteredProductList.slice(0, endIndex)
  }

  return (
    <BusinessProductsSection>
      <LocationBasedProductSection
        title={new MessageFormat(worldready, {
          id: 'pages/businesses.businesses.local.fullpage.title',
        }).format()}
        subtitle={new MessageFormat(worldready, {
          id: 'pages/businesses.businesses.local.fullpage.subtitle',
        }).format({
          numberOfBusinesses: filteredProductList.length,
        })}
        location={location}
        history={history}
        basepath="/"
        locationName={
          searchLocationName === NO_LOCATION_DEFAULT_LOCATION_TEXT
            ? ''
            : searchLocationName
        }
        productList={getProductsInPage()}
        productDisplayType="IMAGE_ONLY"
        editableLocation={true}
        editableSearchLabel={new MessageFormat(worldready, {
          id: 'pages/businesses.businesses.hero.search.label',
        }).format()}
        editableSearchButtonLabel={new MessageFormat(worldready, {
          id: 'pages/businesses.businesses.local.fullpage.editable.button',
        }).format()}
        noSearchResultsTitle={new MessageFormat(worldready, {
          id: 'pages/businesses.businesses.local.no.results.title',
        }).format()}
        noSearchResultsSubtitle={new MessageFormat(worldready, {
          id: 'pages/businesses.businesses.local.no.results.subtitle',
        }).format()}
        noSearchResultsLabel={new MessageFormat(worldready, {
          id: 'pages/businesses.businesses.hero.search.label',
        }).format()}
        onSaveEditLocation={onSaveEditLocation}
        showLoadingSpinner={showSpinner}
        displayNameAndAddressCaption={true}
        currentLocationLabel={new MessageFormat(worldready, {
          id: 'pages/businesses.businesses.local.currentLocation',
        }).format()}
      />
      {currentPage === maxNumberOfPages() && (
        <VoucherSection
          showFiltered={true}
          storesData={lodash.get(storesData, 'getStoresByLocation.stores', [])}
          locationName={
            searchLocationName === NO_LOCATION_DEFAULT_LOCATION_TEXT
              ? ''
              : searchLocationName
          }
        />
      )}
    </BusinessProductsSection>
  )
}

BusinessesSearch.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
  }).isRequired,
  history: PropTypes.object.isRequired,
}

export default BusinessesSearch
